import type { SupabaseClient, PostgrestError } from '@supabase/supabase-js';

/**
 * Network Retry Utility
 * Provides retry logic with exponential backoff + jitter for all network calls
 * Includes 10s timeout and 3 retries by default
 * Wrapped with circuit breaker to prevent retry amplification
 */

type CircuitState = 'CLOSED' | 'OPEN' | 'HALF_OPEN';

class ClientCircuitBreaker {
  private failures = 0;
  private successes = 0;
  private lastFailureTime = 0;
  private state: CircuitState = 'CLOSED';
  private failureThreshold = 5;
  private recoveryTimeoutMs = 60000;
  private successThreshold = 2;

  async execute<T>(fn: () => Promise<T>): Promise<T> {
    if (this.state === 'OPEN') {
      if (Date.now() - this.lastFailureTime > this.recoveryTimeoutMs) {
        this.state = 'HALF_OPEN';
        this.successes = 0;
      } else {
        throw new Error('Circuit breaker is OPEN');
      }
    }

    try {
      const result = await fn();
      this.onSuccess();
      return result;
    } catch (error) {
      this.onFailure();
      throw error;
    }
  }

  private onSuccess() {
    this.failures = 0;
    if (this.state === 'HALF_OPEN') {
      this.successes++;
      if (this.successes >= this.successThreshold) {
        this.state = 'CLOSED';
        this.successes = 0;
      }
    }
  }

  private onFailure() {
    this.failures++;
    this.lastFailureTime = Date.now();
    if (this.state === 'HALF_OPEN') {
      this.state = 'OPEN';
      this.successes = 0;
      return;
    }
    if (this.failures >= this.failureThreshold) {
      this.state = 'OPEN';
    }
  }
}

const circuitBreakers = new Map<string, ClientCircuitBreaker>();

function getCircuitBreaker(key: string): ClientCircuitBreaker {
  if (!circuitBreakers.has(key)) {
    circuitBreakers.set(key, new ClientCircuitBreaker());
  }
  return circuitBreakers.get(key)!;
}

type SupabaseRetryError = Error & Partial<PostgrestError> & { status?: number };

// Retry configuration
export interface RetryConfig {
  maxRetries?: number;
  timeoutMs?: number;
  baseDelayMs?: number;
  maxDelayMs?: number;
}

const DEFAULT_CONFIG: Required<RetryConfig> = {
  maxRetries: 3,
  timeoutMs: 15000, // 15 seconds - increased to handle slower connections
  baseDelayMs: 500, // 500ms - reduced retry delay for faster recovery
  maxDelayMs: 30000, // 30 seconds
};

/**
 * Calculate exponential backoff with jitter
 */
function calculateDelay(attempt: number, config: Required<RetryConfig>): number {
  const exponentialDelay = Math.min(config.baseDelayMs * Math.pow(2, attempt), config.maxDelayMs);

  // Add jitter (±25% of delay)
  const jitter = exponentialDelay * 0.25 * (Math.random() * 2 - 1);
  return Math.floor(exponentialDelay + jitter);
}

/**
 * Sleep utility
 */
function sleep(ms: number): Promise<void> {
  return new Promise((resolve) => setTimeout(resolve, ms));
}

function normalizeRetryError(error: unknown): SupabaseRetryError {
  if (error instanceof Error) {
    return error as SupabaseRetryError;
  }

  if (error && typeof error === 'object') {
    const errObj = error as Partial<PostgrestError> & { status?: number };
    const wrappedError = new Error(errObj.message || 'Unknown error') as SupabaseRetryError;
    Object.assign(wrappedError, error);
    return wrappedError;
  }

  return new Error(String(error) || 'Unknown error') as SupabaseRetryError;
}

function toPostgrestError(error: SupabaseRetryError | null): PostgrestError | null {
  if (!error) return null;

  const isTimeout = /timeout/i.test(error.message);
  return {
    message: error.message,
    details: error.details ?? '',
    hint: error.hint ?? '',
    code: error.code ?? (isTimeout ? 'TIMEOUT' : 'UNKNOWN'),
    ...(typeof error.status === 'number' ? { status: error.status } : {}),
  } as PostgrestError;
}

/**
 * Wrapper for fetch with timeout and retry
 */
export async function fetchWithRetry(
  url: string,
  options: RequestInit = {},
  config: RetryConfig = {},
): Promise<Response> {
  const domain = new URL(url).hostname;
  const circuitBreaker = getCircuitBreaker(domain);

  return circuitBreaker.execute(async () => {
    const finalConfig = { ...DEFAULT_CONFIG, ...config };
    let lastError: Error | null = null;

    for (let attempt = 0; attempt <= finalConfig.maxRetries; attempt++) {
      try {
        const controller = new AbortController();
        const timeoutId = setTimeout(() => controller.abort(), finalConfig.timeoutMs);

        const response = await fetch(url, {
          ...options,
          signal: controller.signal,
        });

        clearTimeout(timeoutId);
        return response;
      } catch (error) {
        lastError = error instanceof Error ? error : new Error('Unknown error');

        if (attempt < finalConfig.maxRetries) {
          const delay = calculateDelay(attempt, finalConfig);
          await sleep(delay);
          continue;
        }
      }
    }

    throw lastError || new Error('Max retries exceeded');
  });
}

/**
 * Wrapper for Supabase query with retry
 */
export async function supabaseQueryWithRetry<T>(
  queryFn: () => PromiseLike<{ data: T | null; error: PostgrestError | null }>,
  config: RetryConfig = {},
): Promise<{ data: T | null; error: PostgrestError | null }> {
  const finalConfig = { ...DEFAULT_CONFIG, ...config };
  let lastError: SupabaseRetryError | null = null;

  for (let attempt = 0; attempt <= finalConfig.maxRetries; attempt++) {
    try {
      const timeoutPromise = new Promise<never>((_, reject) =>
        setTimeout(() => reject(new Error('Query timeout')), finalConfig.timeoutMs),
      );

      const result = await Promise.race([Promise.resolve(queryFn()), timeoutPromise]);

      if (result.error) {
        throw result.error;
      }

      return result;
    } catch (error) {
      lastError = normalizeRetryError(error);

      if (attempt < finalConfig.maxRetries) {
        const delay = calculateDelay(attempt, finalConfig);
        await sleep(delay);
        continue;
      }
    }
  }

  return { data: null, error: toPostgrestError(lastError) };
}

/**
 * Wrapper for Supabase RPC with retry
 */
export async function supabaseRpcWithRetry<T>(
  client: SupabaseClient,
  functionName: string,
  params?: Record<string, unknown>,
  config: RetryConfig = {},
): Promise<{ data: T | null; error: PostgrestError | null }> {
  return supabaseQueryWithRetry(
    () =>
      client.rpc(functionName, params) as unknown as Promise<{
        data: T | null;
        error: PostgrestError | null;
      }>,
    config,
  );
}

/**
 * Wrapper for Supabase Functions invoke with retry
 */
export async function supabaseFunctionWithRetry<T = unknown>(
  client: SupabaseClient,
  functionName: string,
  options?: {
    body?: Record<string, unknown>;
    headers?: Record<string, string>;
  },
  config: RetryConfig = {},
): Promise<{ data: T | null; error: Error | null }> {
  const finalConfig = { ...DEFAULT_CONFIG, ...config };
  let lastError: Error | null = null;

  for (let attempt = 0; attempt <= finalConfig.maxRetries; attempt++) {
    try {
      const controller = new AbortController();
      const timeoutId = setTimeout(() => controller.abort(), finalConfig.timeoutMs);

      const result = await client.functions.invoke<T>(functionName, {
        ...options,
        signal: controller.signal,
      });

      clearTimeout(timeoutId);

      // Check if result has 429 status before throwing error
      // Supabase Functions may return 429 in the result object itself
      const resultObj = result as unknown as {
        status?: number;
        response?: { status?: number };
        data?: { status?: number };
        error?: Error;
      };
      const resultStatus =
        resultObj?.status || resultObj?.response?.status || resultObj?.data?.status;
      if (resultStatus === 429) {
        const rateLimitError = new Error('Too Many Requests (429)') as Error & { status: number };
        rateLimitError.status = 429;
        return { data: null, error: rateLimitError };
      }

      if (result.error) {
        // Check if the error itself indicates 429 before throwing
        const errorObj = result.error as Error & {
          status?: number;
          context?: { status?: number; statusCode?: number };
          response?: { status?: number };
        };
        const errorStatus =
          errorObj?.status ||
          errorObj?.context?.status ||
          errorObj?.context?.statusCode ||
          errorObj?.response?.status;
        if (errorStatus === 429) {
          const rateLimitError = new Error('Too Many Requests (429)') as Error & { status: number };
          rateLimitError.status = 429;
          return { data: null, error: rateLimitError };
        }
        throw result.error;
      }

      return result as { data: T | null; error: Error | null };
    } catch (error) {
      lastError = error instanceof Error ? error : new Error('Unknown error');

      // Check if error is a 429 (Too Many Requests) rate limit error
      // Rate limit errors should not be retried immediately as they indicate
      // the server is limiting requests. Retrying would worsen the situation.
      const errorObj = error as Error & {
        status?: number;
        context?: { status?: number; statusCode?: number };
        response?: { status?: number };
        name?: string;
      };
      const isRateLimitError =
        errorObj?.status === 429 ||
        errorObj?.context?.status === 429 ||
        errorObj?.context?.statusCode === 429 ||
        errorObj?.response?.status === 429 ||
        errorObj?.message?.includes('429') ||
        errorObj?.message?.includes('Too Many Requests') ||
        errorObj?.message?.includes('rate limit') ||
        (errorObj?.name === 'FunctionsHttpError' &&
          (errorObj?.context?.status === 429 || errorObj?.context?.statusCode === 429));

      if (isRateLimitError) {
        // Return rate limit error immediately without retrying
        return { data: null, error: lastError };
      }

      if (attempt < finalConfig.maxRetries) {
        const delay = calculateDelay(attempt, finalConfig);
        await sleep(delay);
        continue;
      }
    }
  }

  return { data: null, error: lastError };
}
